import type { Rng } from '../../core/rng';
import type { Vec2 } from '../../geo/types';
import type { SourceKind } from './types';

export const LOT = {
  frontage: {
    street: { min: 4, mode: 5.5, max: 8 },
    alley: { min: 4, mode: 5, max: 6 },
  },
  depth: {
    street: { min: 6, max: 22 },
    alley: { min: 4, max: 12 },
  },
  shrink: {
    depthStep: 1,
    minDepthStreet: 6,
    minDepthAlley: 4,
    minFrontage: 4,
    advanceStep: 1,
  },
  sourceOffset: 0.05,
  overlapTolerance: 0.05,
} as const;

export const SOURCE_PRIORITY: Record<SourceKind, number> = {
  quay: 0,
  street: 1,
  dike: 3,
  road: 3,
  alley: 4,
  shore: 5,
  square: 5,
};

export function sampleFrontage(kind: SourceKind, rng: Rng): number {
  const isAlley = kind === 'alley';
  const cfg = isAlley ? LOT.frontage.alley : LOT.frontage.street;

  const u = rng.next();
  if (u < 0.5) {
    return cfg.min + Math.sqrt(u * 2) * (cfg.mode - cfg.min);
  } else {
    return cfg.max - Math.sqrt((1 - u) * 2) * (cfg.max - cfg.mode);
  }
}

export function sampleDepth(kind: SourceKind, rng: Rng): number {
  const isAlley = kind === 'alley';
  const cfg = isAlley ? LOT.depth.alley : LOT.depth.street;
  return rng.range(cfg.min, cfg.max);
}

export function minDepthFor(kind: SourceKind): number {
  return kind === 'alley' ? LOT.shrink.minDepthAlley : LOT.shrink.minDepthStreet;
}

/*
 * Channels narrow and widen along their length, so the width is read where a lot stands: `s`
 * metres along the centreline, interpolated between the widths at the vertices either side.
 */
export function widthAtDistance(
  centerline: readonly Vec2[],
  widths: readonly number[],
  s: number
): number {
  if (widths.length === 0) return 0;
  let start = 0;
  for (let i = 0; i < Math.min(centerline.length, widths.length) - 1; i++) {
    const a = centerline[i]!;
    const b = centerline[i + 1]!;
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (s <= start + length) {
      const t = length > 0 ? Math.max(0, (s - start) / length) : 0;
      return widths[i]! + (widths[i + 1]! - widths[i]!) * t;
    }
    start += length;
  }
  return widths[widths.length - 1]!;
}
